import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";

import { Pagination } from "@/components/Pagination";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  Page,
  Spinner,
  Table,
  Tag,
} from "@/components/ui";
import { formatDate } from "@/lib/format";
import { useCardImports, useUploadCardImport } from "@/tarjetas/queries";

/** The server refuses more; this says so before the upload, not after it. */
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * *Importar Excel* — the first step: name the import and pick the workbook.
 *
 * The upload answers with the workbook's columns, and the next screen is where
 * they are mapped to card fields. Below, the imports already made, each with
 * the report of what it brought in.
 */
export function ImportarTarjetas() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page") ?? 1) || 1);
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const upload = useUploadCardImport();
  const imports = useCardImports(page);

  async function submit() {
    setFailure(null);
    if (!name.trim() || !file) {
      setFailure(t("cards.import.needNameAndFile"));
      return;
    }
    if (!/\.xlsx?$/i.test(file.name)) {
      setFailure(t("cards.import.wrongType"));
      return;
    }
    if (file.size > MAX_BYTES) {
      setFailure(t("cards.import.tooBig"));
      return;
    }
    try {
      const created = await upload.mutateAsync({ name: name.trim(), file });
      void navigate(`/tarjetas/importaciones/${created.id}`);
    } catch (problem) {
      setFailure(problem instanceof Error ? problem.message : t("errors.uploadCards"));
    }
  }

  return (
    <Page
      title={t("cards.import.title")}
      subtitle={
        <Link to="/tarjetas" className="text-brand-700">
          {t("cards.backToList")}
        </Link>
      }
    >
      <div className="grid gap-8">
        <Card title={t("cards.import.uploadTitle")}>
          <form
            className="grid gap-5 px-4 py-4 sm:grid-cols-2 sm:gap-x-6"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <FormField label={t("cards.import.name")} hint={t("cards.import.nameHint")}>
              {(id) => (
                <Input
                  id={id}
                  value={name}
                  maxLength={200}
                  onChange={(event) => setName(event.target.value)}
                />
              )}
            </FormField>
            <FormField label={t("cards.import.file")} hint={t("cards.import.fileHint")}>
              {(id) => (
                <Input
                  id={id}
                  type="file"
                  accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                />
              )}
            </FormField>
            {failure ? (
              <p
                role="alert"
                className="mb-0 border-2 border-brand px-3 py-2 text-sm sm:col-span-2"
              >
                {failure}
              </p>
            ) : null}
            <div className="sm:col-span-2">
              <Button type="submit" variant="primary" disabled={upload.isPending}>
                {upload.isPending
                  ? t("cards.import.uploading")
                  : t("cards.import.upload")}
              </Button>
            </div>
          </form>
        </Card>

        <Card title={t("cards.import.historyTitle")}>
          {imports.isPending ? (
            <Spinner label={t("common.loading")} />
          ) : imports.isError ? (
            <ErrorState
              message={t("errors.cardImports")}
              onRetry={() => void imports.refetch()}
            />
          ) : imports.data.results.length === 0 ? (
            <EmptyState>{t("cards.import.noHistory")}</EmptyState>
          ) : (
            <>
              <Table>
                <thead>
                  <tr>
                    <th>{t("cards.import.name")}</th>
                    <th className="hidden md:table-cell">{t("cards.import.file")}</th>
                    <th>{t("cards.import.cards")}</th>
                    <th className="hidden sm:table-cell">
                      {t("cards.column.created")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {imports.data.results.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <Link
                          to={`/tarjetas/importaciones/${item.id}`}
                          className="font-display font-extrabold"
                        >
                          {item.name}
                        </Link>
                        {item.status === "pending" ? (
                          <Tag tone="outline" className="ml-2">
                            {t("cards.import.pending")}
                          </Tag>
                        ) : null}
                        {item.created_by_agent_name ? (
                          <div className="text-xs text-mute-600">
                            {item.created_by_agent_name}
                          </div>
                        ) : null}
                      </td>
                      <td className="hidden text-sm md:table-cell">
                        {item.file_name || t("common.none")}
                      </td>
                      <td className="tabular-nums">
                        {item.card_count > 0 ? (
                          <Link to={`/tarjetas?importacion=${item.id}`}>
                            {item.card_count.toLocaleString("es-MX")}
                          </Link>
                        ) : (
                          0
                        )}
                      </td>
                      <td className="hidden text-sm whitespace-nowrap sm:table-cell">
                        {formatDate(item.created)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination
                page={page}
                count={imports.data.count}
                shown={imports.data.results.length}
                isFetching={imports.isFetching}
                onChange={(next) => setParams({ page: String(next) })}
              />
            </>
          )}
        </Card>
      </div>
    </Page>
  );
}
